import React, { useEffect, useContext, useState } from "react";
import { AuthContext } from "../../contexts/auth";
import { Link } from "wouter";
import moment from "moment";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

import Fetching from "../../components/fetching";
import Empty from "../../components/empty";
import Footer from "../../components/footer";
import { Popover } from "@headlessui/react";

import { months } from "../../config";
import { V2PayrollsYearsList, V2PayrollMonthList } from "../../services/api_dipendenti";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function monthLabel(m) {
  return months.find((x) => x.value === m)?.label ?? String(m);
}

export default function ArCedolini() {
  const { token } = useContext(AuthContext);

  const [years, setYears] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [openYear, setOpenYear] = useState(null);

  // cache per anno: { [year]: { loading: bool, list: Payroll[], count: number } }
  const [yearData, setYearData] = useState({});

  useEffect(() => {
    if (!token) return;

    (async () => {
      setLoadingYears(true);
      try {
        const data = await V2PayrollsYearsList(token);
        const ordered = Array.isArray(data) ? [...data].sort((a, b) => b.year - a.year) : [];
        setYears(ordered);
      } catch (e) {
        setYears([]);
      } finally {
        setLoadingYears(false);
      }
    })();
  }, [token]);

  async function loadYearIfNeeded(year) {
    if (yearData?.[year]?.list && !yearData?.[year]?.loading) return;

    setYearData((prev) => ({
      ...prev,
      [year]: { loading: true, list: prev?.[year]?.list || [], count: prev?.[year]?.count || 0 },
    }));

    try {
      const resp = await V2PayrollMonthList(token, year);
      const list = Array.isArray(resp) ? resp : resp?.results || [];
      const count = Number(resp?.count ?? list.length ?? 0);

      const sorted = list
        .slice()
        .sort((a, b) => {
          const ya = a.anno ?? a.year;
          const yb = b.anno ?? b.year;
          if (yb !== ya) return yb - ya;
          const ma = a.mese ?? a.month;
          const mb = b.mese ?? b.month;
          return mb - ma;
        });

      setYearData((prev) => ({
        ...prev,
        [year]: { loading: false, list: sorted, count },
      }));
    } catch (e) {
      setYearData((prev) => ({
        ...prev,
        [year]: { loading: false, list: [], count: 0 },
      }));
    }
  }

  const hasAny = years.reduce((acc, y) => acc + (Number(y.count) || 0), 0) > 0;

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex-grow pb-8">
        <Popover as="header" className={`bg-indigo-200`}>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
              <div className="w-full -5 lg:border-t lg:border-white lg:border-opacity-20">
                <div className="flex justify-center my-2 py-2">
                  <h2 className="text-center text-2xl font-semibold text-indigo-800">I Miei Cedolini</h2>
                </div>
              </div>
            </div>
          </div>
        </Popover>

        <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 mt-8">
          <div className="overflow-visible rounded-xl border border-gray-300 bg-white">
            <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <h3 className="text-base font-semibold uppercase text-indigo-800">I miei cedolini</h3>
                <p className="mt-1 text-xs text-gray-500">Seleziona un anno per vedere i cedolini disponibili</p>
              </div>
            </div>
          </div>

          {loadingYears && (
            <div className="pt-4">
              <Fetching />
            </div>
          )}

          {!loadingYears && !hasAny && (
            <div className="pt-4">
              <Empty />
            </div>
          )}

          {!loadingYears && hasAny && (
            <div className="mt-6">
              <div className="mt-6 space-y-3">
                {years.map((y) => {
                  const isOpen = openYear === y.year;
                  const detail = yearData[y.year] || { loading: false, list: [], count: 0 };

                  return (
                    <div
                      key={y.year}
                      className={classNames(
                        "rounded-xl border shadow-sm overflow-hidden",
                        isOpen ? "border-indigo-200" : "border-gray-300"
                      )}
                    >
                      {/* Header anno */}
                      <button
                        type="button"
                        onClick={async () => {
                          const willOpen = openYear !== y.year;
                          setOpenYear((prev) => (prev === y.year ? null : y.year));
                          if (willOpen) await loadYearIfNeeded(y.year);
                        }}
                        className={classNames(
                          "w-full flex items-center justify-between gap-3 px-4 py-4 text-left",
                          isOpen ? "bg-indigo-50" : "bg-white hover:bg-gray-50"
                        )}
                      >
                        <div className="flex flex-col">
                          <span className="text-lg font-semibold text-gray-900">{y.year}</span>
                          <span className="text-xs text-gray-500">
                            {y.count} cedolin{y.count === 1 ? "o" : "i"}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                            {y.count}
                          </span>
                          {isOpen ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                          )}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="bg-white px-4 pb-4 pt-2">
                          {detail.loading ? (
                            <div className="py-6 text-center text-sm italic text-gray-500">
                              Caricamento...
                            </div>
                          ) : detail.list.length === 0 ? (
                            <div className="py-6 text-center text-sm italic text-gray-500">
                              Nessun cedolino disponibile per {y.year}
                            </div>
                          ) : (
                            <div className="mt-2 overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                  <tr className="text-left text-xs font-semibold text-gray-600">
                                    <th className="py-2 px-3">Competenza</th>
                                    <th className="py-2 pr-3">Letta</th>
                                    <th className="py-2 px-4 text-right">Azioni</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {detail.list.map((cedolino) => {
                                    const mese = cedolino.mese ?? cedolino.month;
                                    const anno = cedolino.anno ?? cedolino.year;

                                    return (
                                      <tr key={cedolino.id} className="hover:bg-gray-50">
                                        <td className="py-3 px-3">
                                          <div className="text-sm font-medium text-gray-900">
                                            {monthLabel(mese)} {anno}
                                          </div>
                                          {cedolino.created_at && (
                                            <div className="text-xs text-gray-500">
                                              Caricato:{" "}
                                              {moment(cedolino.created_at).format("DD/MM/YYYY HH:mm")}
                                            </div>
                                          )}
                                        </td>

                                        <td className="py-3 pr-3">
                                          {cedolino.read_at ? (
                                            <div className="inline-flex items-center gap-1 text-sm text-gray-700">
                                              <CheckCircleIcon className="h-5 w-5 text-teal-500" />
                                              <span className="hidden sm:inline">
                                                {moment(cedolino.read_at).format("DD/MM/YYYY HH:mm")}
                                              </span>
                                            </div>
                                          ) : (
                                            <div className="inline-flex items-center gap-1 text-sm text-gray-700">
                                              <XCircleIcon className="h-5 w-5 text-red-500" />
                                              <span className="text-red-600">Non letta</span>
                                            </div>
                                          )}
                                        </td>

                                        <td className="p-3 text-right">
                                          <Link
                                            to={`/areariservata/cedolini/${cedolino.id}`}
                                            className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                          >
                                            Leggi
                                          </Link>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}